import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import moment from "moment";
import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import LanguageContext from "@/context/language/LanguageContext";
import useResponse from "@/shared/hooks/useResponse";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { Filter, ROUTES, useDateTime } from "../../../shared";
import { FILTER_KEYS, FILTER_NAMES } from "../types";

const useFilteredValues = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { currentDateFormat } = useDateTime();
  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();
  const { language } = useContext(LanguageContext);

  const { params, setParams, fetchData } = useTableStoreByRoute(
    location.pathname as ROUTES
  );
  const filterParams = params.filters;

  const [filteredValues, setFilteredValues] = useState<Filter[]>([]);

  const dateRangeFilterObject = {
    id: FILTER_KEYS.Date,
    name: FILTER_KEYS.Date
  };

  const processFilterValues = (
    filterValues: Filter[],
    filteredValues: Filter[]
  ) => {
    let hasDateRange = false;
    let hasBothDateRangeValues = false;

    const validFilterValues = Array.isArray(filterValues)
      ? filterValues.filter(Boolean)
      : [];

    validFilterValues.forEach((v: Filter) => {
      hasDateRange =
        v.name === FILTER_KEYS.DateRangeFrom ||
        v.name === FILTER_KEYS.DateRangeTo;
      hasBothDateRangeValues =
        v.name !== FILTER_KEYS.DateRangeFrom &&
        v.name !== FILTER_KEYS.DateRangeTo;

      if (hasBothDateRangeValues) {
        filteredValues.push(v);
      }
    });

    return { hasDateRange, filteredValues };
  };

  const addDateRangeFilter = (val: any, filteredValues: Filter[]) => {
    if (!val?.[0] || !val?.[1]) {
      return;
    }

    const dateRangeDisplayValue = {
      id: FILTER_KEYS.Date,
      name: t("Filter##from to date", {
        fromDate: moment(val[0].id).format(currentDateFormat),
        toDate: moment(val[1].id).format(currentDateFormat)
      })
    };
    filteredValues.push(dateRangeDisplayValue);
  };

  const getFilteredValues = (filterObject: any) => {
    const filteredValues: Filter[] = [];
    if (filterObject) {
      for (const val of Object.values(filterObject)) {
        const filterValues: Filter[] = val as Filter[];

        const { hasDateRange, filteredValues: updatedFilteredValues } =
          processFilterValues(filterValues, filteredValues);

        if (hasDateRange) {
          addDateRangeFilter(val, updatedFilteredValues);
        }
      }
    }
    return filteredValues;
  };

  useEffect(() => {
    setFilteredValues(getFilteredValues(filterParams));
  }, [filterParams, language]);

  const updateCurrentFilters = (
    key: string,
    filteredValuesWithoutCurrentItem: Filter[],
    currentFilters: {
      [key: string]: any;
    }
  ) => ({
    ...currentFilters,
    [key]: filteredValuesWithoutCurrentItem
  });

  const mapThroughFiltersAndRemoveValue = (
    currentFilters: { [key: string]: any },
    filterItem: Filter
  ) => {
    const dateRangeItem = filterItem?.id === FILTER_KEYS.Date; // check if date range filter is clicked

    let allFilters: { [key: string]: any } = currentFilters;

    for (const [key, val] of Object.entries(currentFilters)) {
      let values: any = val;

      const dateRangeValues = values.filter(
        (v: Filter) =>
          v.name === FILTER_KEYS.DateRangeFrom ||
          v.name === FILTER_KEYS.DateRangeTo
      );

      if (
        currentFilters.hasOwnProperty(FILTER_NAMES.DateRange) &&
        dateRangeItem &&
        dateRangeValues.length > 0
      ) {
        const emptyDateRangeArray: Filter[] = [];

        allFilters = updateCurrentFilters(
          key,
          emptyDateRangeArray,
          currentFilters
        );

        //  else - remove only the clicked filter value
      } else if (values.includes(filterItem)) {
        const filteredValuesWithoutCurrentItem = values.filter(
          (val: Filter) => val !== filterItem
        );

        allFilters = updateCurrentFilters(
          key,
          filteredValuesWithoutCurrentItem,
          currentFilters
        );
      }
    }

    // Update filters in the store
    setParams({ filters: allFilters });
  };

  const resetCurrentPage = () => {
    setParams({ pageNumber: "1" });
    fetchData(getData, handleResponse);
  };

  const handleDeleteFilterValue = (filterItem: Filter) => {
    const currentFilters = filterParams;

    if (currentFilters) {
      mapThroughFiltersAndRemoveValue(currentFilters, filterItem);
      resetCurrentPage();
    }
  };

  return {
    filteredValues,
    mapThroughFiltersAndRemoveValue,
    handleDeleteFilterValue,
    resetCurrentPage,
    dateRangeFilterObject
  };
};

export default useFilteredValues;
